import React, { useEffect, useState, useCallback, useRef } from 'react';
import Typography from "apollo-react/components/Typography";
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import Select from 'apollo-react/components/Select';
import MenuItem from 'apollo-react/components/MenuItem';
import Grid from 'apollo-react/components/Grid';
import Checkbox from 'apollo-react/components/Checkbox';
import Autocomplete from 'apollo-react/components/Autocomplete';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Box from 'apollo-react/components/Box';
import NewspaperFold from 'apollo-react-icons/NewspaperFold';
import PresentationSpeaker from 'apollo-react-icons/PresentationSpeaker';
import Planner from 'apollo-react-icons/Planner';

import tabStyles from './ECTD.module.css';
import EctdUpload from "../../Guest/Requests/New_Requests/eCTD/EctdUpload";
import EctdTree from "./Ectd";
import EctdVersionsHistory from "./EctdVersionsHistory";
import MultiFormContext from '../../../contexts/MultiFormContext';
import { useContext } from 'react';
import axiosInstance from '../../../axiosInstance';
import CommentsList from '../FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from '../CommentToggle/CommentToggle';
import { ROLES } from '../../../constants/roles';
import { CATEGORIES } from '../../../constants/categories';
import { FieldErrorMessage } from '../FieldErrorMessage';
import Divider from 'apollo-react/components/Divider';
import { Paper } from '@mui/material';

// Custom hook to safely use MultiFormContext
const useSafeMultiForm = () => {
    const context = useContext(MultiFormContext);
    return {
        isAvailable: !!context,
        ...context, // Will be undefined if context is null, but that's ok with optional chaining
    };
};

const getTabsForRole = (role) => {
    const baseTabs = [{
        label: "eCTD",
        value: 0
    }];
    
    // Check if user is reviewer or committee member
    const isReviewerOrCommittee = role && (
        role.includes('Assessment') || 
        role.includes('assessment') ||
        role.includes('Validator') || 
        role.includes('validator')
    );
    
    if (isReviewerOrCommittee) {
        baseTabs.push({
            label: "AI Review Agent",
            value: 1
        });
    }
    
    baseTabs.push({
        label: "Versions",
        value: isReviewerOrCommittee ? 2 : 1
    });
    
    return baseTabs;
};

const SUBMISSION_TYPES = [
    "BLA (Biologics License Application)",
    "NDA (New Drug Application)",
    "IND (Investigational New Drug)",
    "510(k) (Premarket Notification)",
    "PMA (Premarket Approval)",
    "De Novo Classification Request",
    "IDE (Investigational Device Exemption)"
];

const BIOLOGIC_PRODUCT_TYPES = [
    "Monoclonal Antibody (mAb)",
    "Antibody-Drug Conjugate (ADC)",
    "Recombinant Protein / Fusion Protein",
    "Vaccine (preventive or therapeutic)",
    "Gene Therapy (AAV, Lentivirus, etc.)",
    "Cell Therapy (CAR-T, stem cell-based, etc.)",
    "Nucleotide-based Product (mRNA, siRNA, ASO)",
    "Plasma-derived Product",
    "Allergenic Extract",
    "Combination Product (biologic-device)",
    "Other (Specify)"
];

const PROPOSED_INDICATIONS = [
    "Oncology - Solid Tumor",
    "Oncology - Hematologic Malignancy",
    "Rare / Genetic Disease",
    "Autoimmune / Inflammatory",
    "Infectious Disease / Virology",
    "Cardiovascular / Metabolic",
    "Neurology / CNS",
    "Ophthalmology",
    "Hematology (non-oncology)",
    "Other (Specify)"
];

const REVIEW_PATHWAYS = [
    "Standard Review",
    "Priority Review"
];

const BASIS_OF_CLINICAL_EFFICACY_OPTIONS = [
    "Two or more adequate and well-controlled Phase 3 trials",
    "One adequate and well-controlled Phase 3 trial",
    "One single-arm trial with compelling results (e.g., in a rare disease)",
    "Evidence from a seamless Phase 2/3 design",
    "Based on biomarker data (for Accelerated Approval)",
    "Real-World Evidence (RWE) as primary support",
    "Other (Specify)"
];

const DOSAGE_FORM_ROUTE_OPTIONS = [
    "Intravenous (IV) Infusion / Injection",
    "Subcutaneous (SC) Injection (pre-filled syringe or vial)",
    "Subcutaneous (SC) Injection (auto-injector / on-body device)",
    "Intramuscular (IM) Injection",
    "Intrathecal / Intracerebroventricular",
    "Intraocular / Intravitreal",
    "Inhalation",
    "Patient-specific product for single administration (e.g., cell/gene therapy)",
    "Other specialized route (Specify)"
];

const Ectd = ({ ectdTreeComponentProps = {}, isEditable = false, initialUploadedFile = null,
    role = '', isSendbackMode = false, ectdFileId = null, requestID = null, requestId = null, 
    hideVersionsTab = false }) => {

    // Use custom safe hook
    const { isAvailable: hasMultiFormContext, ectdUpload: contextEctdUpload,
        setFormDataWithValidation: contextSetFormData, errors } = useSafeMultiForm();

    // Use context data if available, otherwise fall back to props
    const ectdUpload = contextEctdUpload || null;
    const setFormDataWithValidation = contextSetFormData || null;

    const tabs = getTabsForRole(role);
    const [activeTab, setActiveTab] = useState(tabs[0].value);
    const [uploadedFile, setUploadedFile] = useState(initialUploadedFile);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const iframeRef = useRef(null);

    // Use requestID or requestId (both props support for backward compatibility)
    const effectiveRequestId = requestID || requestId;
    
    // Reset iframe loading when switching to eCTD Assistant tab
    useEffect(() => {
        if (activeTab === 1 && tabs.length === 3) {
            setIframeLoading(true);
        }
    }, [activeTab, tabs.length]);

    // Handle postMessage for iframe resizing
    useEffect(() => {
        const handleMessage = (event) => {
            const targetOrigin = "https://iqvia-demo.vercel.app";
            
            if (event.origin !== targetOrigin) {
                return;
            }

            if (event.data && event.data.type === "iframe-resize") {
                if (iframeRef.current && typeof event.data.iframeHeight === "number") {
                    iframeRef.current.style.height = `${event.data.iframeHeight}px`;
                }
            }
        };
        
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormDataWithValidation('ectdUpload', { ...ectdUpload, [name]: value });
    };

    const proposedIndicationOptions = PROPOSED_INDICATIONS
        .filter(item => item !== "Other (Specify)")
        .map(item => ({ label: item }));

    const proposedIndicationValue = (ectdUpload?.proposed_indication || [])
        .filter(item => item)
        .map(item => {
            // Check if this item is from the predefined list
            const isPredefined = PROPOSED_INDICATIONS.includes(item);
            return { 
                label: item,
                custom: !isPredefined // Mark custom options explicitly
            };
        });

    const handleProposedIndicationChange = (event, newValue) => {
        const indications = newValue.map(item => item.label);
        setFormDataWithValidation('ectdUpload', { 
            ...ectdUpload, 
            proposed_indication: indications
        });
    };

    const biologicProductOptions = BIOLOGIC_PRODUCT_TYPES
        .filter(item => item !== "Other (Specify)")
        .map(item => ({ label: item }));

    const biologicProductValue = ectdUpload?.biologic_product_type 
        ? { label: ectdUpload.biologic_product_type }
        : null;

    const handleBiologicProductChange = (event, newValue) => {
        const value = newValue ? newValue.label : '';
        setFormDataWithValidation('ectdUpload', { 
            ...ectdUpload, 
            biologic_product_type: value
        });
    };

    const basisOfEfficacyOptions = BASIS_OF_CLINICAL_EFFICACY_OPTIONS
        .filter(item => item !== "Other (Specify)")
        .map(item => ({ label: item }));

    const basisOfEfficacyValue = ectdUpload?.basis_of_clinical_efficacy 
        ? { label: ectdUpload.basis_of_clinical_efficacy }
        : null;

    const handleBasisOfEfficacyChange = (event, newValue) => {
        const value = newValue ? newValue.label : '';
        setFormDataWithValidation('ectdUpload', { 
            ...ectdUpload, 
            basis_of_clinical_efficacy: value
        });
    };

    const dosageFormOptions = DOSAGE_FORM_ROUTE_OPTIONS
        .filter(item => item !== "Other specialized route (Specify)")
        .map(item => ({ label: item }));

    const dosageFormValue = ectdUpload?.dosage_form_route 
        ? { label: ectdUpload.dosage_form_route }
        : null;

    const handleDosageFormChange = (event, newValue) => {
        const value = newValue ? newValue.label : '';
        setFormDataWithValidation('ectdUpload', { 
            ...ectdUpload, 
            dosage_form_route: value
        });
    };

    // Use comments and setComments from props if available, otherwise use local state
    const [localComments, setLocalComments] = useState([]);
    const comments = ectdTreeComponentProps.comments || localComments;
    const setComments = ectdTreeComponentProps.setComments || setLocalComments;

    // Only for customers and committee members, comments are handled in ectdtreecomponent for internal reviewers
    const fetchComments = useCallback(async () => {
        // Allow fetching for customers and committee members
        const isCustomerOrCommittee = role === 'customer' || 
            (role && (role.includes('Assessment') || role.includes('assessment')));
        if (!isCustomerOrCommittee) return;
        
        // Try to fetch comments by request_id first (shows all versions)
        if (effectiveRequestId) {
            try {
                const response = await axiosInstance.get(`/api/ectd-comments/request/enduser/${effectiveRequestId}`);
                setComments(response.data || []);
                return;
            } catch (error) {
                console.error('Error fetching comments by request ID:', error);
                // Fallback to file-based fetch if available
            }
        }
        
        // Fallback: fetch by uploadedFile id (old approach - single version only)
        if (uploadedFile?.id) {
            try {
                const response = await axiosInstance.get(`/api/ectd-comments/enduser/${uploadedFile.id}`);
                setComments(response.data || []);
            } catch (error) {
                console.error('Error fetching comments by file ID:', error);
            }
        }
    }, [effectiveRequestId, uploadedFile, setComments, role]);

    useEffect(() => {
        if (effectiveRequestId || uploadedFile) {
            fetchComments();
        }
    }, [effectiveRequestId, uploadedFile, fetchComments]);

    useEffect(() => {
        const getFileInfo = async () => {
            try {
                const getFileInfoLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "" : "admin";
                const route = `/api/v1/files/${getFileInfoLink}/${ectdFileId}`;
                const response = await axiosInstance.get(route);
                if (response.data && response.data.success && response?.data?.data)
                    setUploadedFile(response.data.data);
            }
            catch (error) {
                console.error('Error fetching file info:', error);
            }
        }
        if (ectdFileId) {
            getFileInfo()
        }
    }, [ectdFileId, role]);

    // initialization
    useEffect(() => {
        const fetchEctdVersion = async () => {
            try {
                const getLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "" : "admin";
                const route = `/api/v1/ectd-versions/${getLink}`;
                const response = await axiosInstance.get(route);
                if (response.data && response.data.success && response?.data?.data)
                    setUploadedFile(response.data.data);
            }
            catch (error) {
                console.error('Error fetching ECTD version:', error);
            }
        }
        if(requestID) fetchEctdVersion();
    }, [requestID, role])

    const handleAddComment = async (comment, subFormId, fileId = null, fileData = null) => {
        if (!uploadedFile || role !== 'customer') return;

        try {
            const requestData = {
                ectdFileId: uploadedFile?.id,
                comment,
                requestId: effectiveRequestId,
            };

            // Add file ID if provided
            if (fileId) {
                requestData.fileId = fileId;
            }

            const response = await axiosInstance.post(`/api/ectd-comments/enduser`, requestData);

            // Enrich the response with file data if available
            // set isOwner to true since the current user just created this comment
            const enrichedComment = { ...response.data, isOwner: true };
            if (fileData && fileId) {
                // Convert fileData to the expected additional_file format
                enrichedComment.additional_file = {
                    id: fileData.id,
                    original_name: fileData.original_name,
                    file_path: null, // Not provided by upload response
                    file_size: fileData.file_size,
                    mime_type: fileData.mime_type,
                    createdAt: fileData.created_at,
                    updatedAt: fileData.created_at,
                    deletedAt: null,
                    user_id: null, // Will be set by backend
                    end_user_id: null, // Will be set by backend
                    ...fileData // Include any additional fields
                };
            }

            setComments(prev => [enrichedComment, ...prev]);
        } catch (error) {
            console.error("Error adding comment:", error);
            throw error;
        }
    };

    // handle edit comment
    const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
        try {
            const requestData = { comment: newText };

            if (attachmentInfo.remove_attachment) {
                requestData.remove_attachment = true;
            } else if (attachmentInfo.new_file_id) {
                requestData.additional_file_id = attachmentInfo.new_file_id;
            }

            await axiosInstance.put(`/api/ectd-comments/enduser/${commentId}`, requestData);
            fetchComments();
        } catch (error) {
            console.error('error editing comment:', error);
            throw error;
        }
    };

    // handle delete comment
    const handleDeleteComment = async (commentId) => {
        try {
            await axiosInstance.delete(`/api/ectd-comments/enduser/${commentId}`);
            setComments(prev => prev.filter(c => c.id !== commentId));
        } catch (error) {
            console.error('error deleting comment:', error);
            throw error;
        }
    };

    useEffect(() => {
        if (initialUploadedFile)
            setUploadedFile(initialUploadedFile);
    }, [initialUploadedFile]);

    const handleDeleteFile = () => {
        // if we have context, update the form data directly
        if (hasMultiFormContext && setFormDataWithValidation) {
            setFormDataWithValidation('ectdUpload', { ectd_file: [] });
        }
        setDeleteModalOpen(false);
        setUploadedFile(null);
    }

    const getTabIcon = (tabValue) => {
        switch(tabValue) {
            case 0:
                return <NewspaperFold />;
            case 1:
                return tabs.length === 3 ? <PresentationSpeaker /> : <Planner />;
            case 2:
                return <Planner />;
            default:
                return null;
        }
    };

    const handleTabClick = (tabValue) => {
        setActiveTab(tabValue);
    };

    return (
        <div style={{ display: 'flex', gap: '20px'}}>
            {!hideVersionsTab && (
                <div className={tabStyles.tabsContainer}>
                    {tabs.map((tab) => (
                        <div
                            key={tab.value}
                            className={`${tabStyles.tabCard} ${activeTab === tab.value ? tabStyles.active : ''}`}
                            onClick={() => handleTabClick(tab.value)}
                        >
                            <div className={tabStyles.tabIcon}>
                                {getTabIcon(tab.value)}
                            </div>
                            <div className={tabStyles.tabLabel}>
                                {tab.label}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            <Paper elevation={1} sx={{ padding: 4, margin: 0, width: '100%' }}>  
                    {activeTab === 0 && (
                        <>
                            {role === 'customer' && isEditable && (
                                <>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 600, color: '', marginBottom: 2 }}
                                    >
                                        Product Details
                                    </Typography>
                                    <Divider sx={{ marginBottom: 3 }} />
                                </>
                            )}
                            {isEditable && hasMultiFormContext && (
                                <>
                                    <Grid container spacing={2} sx={{ marginBottom: 4 }}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                value={ectdUpload?.sequence_number || ''}
                                                onChange={handleChange}
                                                name="sequence_number"
                                                label="Sequence Number"
                                                required
                                                fullWidth
                                                helperText="Enter a unique sequence number for this eCTD version (e.g., m1/1.2.3)"
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.sequence_number?.error}
                                            />
                                        </Grid>
                                        
                                        {/* <Grid item xs={12} md={6}>
                                            <TextField
                                                value={ectdUpload?.submission_id || ''}
                                                onChange={handleChange}
                                                name="submission_id"
                                                label="Submission ID"
                                                required
                                                fullWidth
                                                helperText="Official FDA-assigned number for the application e.g. BLA 761042"
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.submission_id?.error}
                                            />
                                        </Grid> */}
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                value={ectdUpload?.product_name || ''}
                                                onChange={handleChange}
                                                name="product_name"
                                            label="Product Name"
                                                fullWidth
                                                helperText="Established name of the drug or device"
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.product_name?.error}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <Select
                                                value={ectdUpload?.submission_type || ''}
                                                onChange={handleChange}
                                                name="submission_type"
                                            label="Submission Type"
                                                fullWidth
                                                sx={{
                                                    '& .MuiSelect-select': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>Select submission type</em>
                                                </MenuItem>
                                                {SUBMISSION_TYPES.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.submission_type?.error}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <Autocomplete
                                                value={biologicProductValue}
                                                onChange={handleBiologicProductChange}
                                                source={biologicProductOptions}
                                                label="Biologic Product Type"
                                                placeholder="Select or type custom biologic product type..."
                                            helperText="Select from list or type your own custom type and press Enter"
                                                fullWidth
                                                multiple={false}
                                                allowAddNewOptions
                                                PopperProps={{
                                                    style: { zIndex: 1300 }
                                                }}
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.biologic_product_type?.error}
                                            />
                                        </Grid>

                                        {/* <Grid item xs={12} md={6}>
                                            <Select
                                                value={ectdUpload?.review_pathway || ''}
                                                onChange={handleChange}
                                                name="review_pathway"
                                                label="Review Pathway & Priority"
                                                required
                                                fullWidth
                                                sx={{
                                                    '& .MuiSelect-select': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>Select review pathway</em>
                                                </MenuItem>
                                                {REVIEW_PATHWAYS.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.review_pathway?.error}
                                            />
                                        </Grid> */}
                                        
                                        <Grid item xs={12} md={6}>
                                            <Autocomplete
                                                value={basisOfEfficacyValue}
                                                onChange={handleBasisOfEfficacyChange}
                                                source={basisOfEfficacyOptions}
                                                label="Basis of Clinical Efficacy"
                                                placeholder="Select or type custom basis..."
                                            helperText="Select from list or type your own and press Enter"
                                                fullWidth
                                                multiple={false}
                                                allowAddNewOptions
                                                PopperProps={{
                                                    style: { zIndex: 1300 }
                                                }}
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.basis_of_clinical_efficacy?.error}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Autocomplete
                                                value={dosageFormValue}
                                                onChange={handleDosageFormChange}
                                                source={dosageFormOptions}
                                                label="Dosage Form & Route of Administration"
                                                placeholder="Select or type custom dosage form..."
                                            helperText="Select from list or type your own and press Enter"
                                                fullWidth
                                                multiple={false}
                                                allowAddNewOptions
                                                PopperProps={{
                                                    style: { zIndex: 1300 }
                                                }}
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.dosage_form_route?.error}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Autocomplete
                                                value={proposedIndicationValue}
                                                onChange={handleProposedIndicationChange}
                                                source={proposedIndicationOptions}
                                                label="Proposed Indication"
                                                placeholder="Select from list or type to add custom indication..."
                                            helperText="Select predefined options or type your own custom indication and press Enter. Selected options appear as chips below."
                                                fullWidth
                                                multiple
                                                allowAddNewOptions
                                                showSelectAll
                                                showClearAll
                                                limitChips={3}
                                                clearOnBlur={false}
                                                filterSelectedOptions={false}
                                                PopperProps={{
                                                    style: { zIndex: 1300 }
                                                }}
                                            />
                                            <FieldErrorMessage
                                                error={errors.ectdUpload?.fields?.proposed_indication?.error}
                                            />
                                        </Grid>
                                        
                                        {/* <Grid item xs={12}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    marginTop: 2, 
                                                    marginBottom: 1,
                                                    fontWeight: 400,
                                                    color: 'rgba(0, 0, 0, 0.87)',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                            Define the objective of your current review activity
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="Clinical"
                                                checked={ectdUpload?.clinical || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, clinical: checked });
                                                }}
                                                name="clinical"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="CMC / Quality"
                                                checked={ectdUpload?.cmc_quality || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, cmc_quality: checked });
                                                }}
                                                name="cmc_quality"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="Non-Clinical / Toxicology"
                                                checked={ectdUpload?.non_clinical_toxicology || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, non_clinical_toxicology: checked });
                                                }}
                                                name="non_clinical_toxicology"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="Clinical Pharmacology"
                                                checked={ectdUpload?.clinical_pharmacology || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, clinical_pharmacology: checked });
                                                }}
                                                name="clinical_pharmacology"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="Biostatistics"
                                                checked={ectdUpload?.biostatistics || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, biostatistics: checked });
                                                }}
                                                name="biostatistics"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="Device / Engineering"
                                                checked={ectdUpload?.device_engineering || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, device_engineering: checked });
                                                }}
                                                name="device_engineering"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Checkbox
                                                label="Labeling"
                                                checked={ectdUpload?.labeling || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, labeling: checked });
                                                }}
                                                name="labeling"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid> */}
                                        
                                        {/* <Grid item xs={12}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    marginTop: 2, 
                                                    marginBottom: 1,
                                                    fontWeight: 400,
                                                    color: 'rgba(0, 0, 0, 0.87)',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                Also check any applicable expedited programs
                                            </Typography>
                                        </Grid> */}
                                        {/* <Grid item xs={12} md={6}>
                                            <Checkbox
                                                label="Fast Track Designation"
                                                checked={ectdUpload?.fast_track || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, fast_track: checked });
                                                }}
                                                name="fast_track"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Checkbox
                                                label="Breakthrough Therapy Designation"
                                                checked={ectdUpload?.breakthrough_therapy || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, breakthrough_therapy: checked });
                                                }}
                                                name="breakthrough_therapy"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Checkbox
                                                label="Regenerative Medicine Advanced Therapy (RMAT)"
                                                checked={ectdUpload?.rmat || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, rmat: checked });
                                                }}
                                                name="rmat"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Checkbox
                                                label="Accelerated Approval"
                                                checked={ectdUpload?.accelerated_approval || false}
                                                onChange={(e, checked) => {
                                                    setFormDataWithValidation('ectdUpload', { ...ectdUpload, accelerated_approval: checked });
                                                }}
                                                name="accelerated_approval"
                                                size="small"
                                                sx={{ 
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid> */}
                                    </Grid>
                                </>
                            )}
                            
                            {role === 'customer' && isEditable && (
                                <>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 600, color: '', marginBottom: 2, marginTop: 4 }}
                                    >
                                        Upload eCTD Document
                                    </Typography>
                                    <Divider sx={{ marginBottom: 3 }} />
                                </>
                            )}
                            
                            {uploadedFile === null && isEditable ? (
                                <EctdUpload />
                            ) : (
                                <EctdTree 
                                    {...ectdTreeComponentProps}
                                    role={role}
                                    ectdFileId={uploadedFile?.id}
                                    uploadedFile={uploadedFile}
                                    handleDeleteFile={() => setDeleteModalOpen(true)}
                                    comments={comments}
                                    setComments={setComments}
                                    ectdVersion={ectdTreeComponentProps.ectdVersion}
                                    isSendbackMode={isSendbackMode}
                                />
                            )}
                            {/* comments section */}
                            {role === 'customer' && (
                                <div
                                    className="form-comment-section"
                                    style={{
                                        isolation: 'isolate',
                                        position: 'relative',
                                        zIndex: 1,
                                        marginTop: '35px'
                                    }}
                                >
                                    <CommentsList
                                        prevComments={comments}
                                        pagination={{}}
                                        filters={{}}
                                        updateFilters={() => { }}
                                        showControls={false}
                                        showFilters={false}
                                        showSorting={false}
                                        showPageSize={false}
                                        showResultsInfo={false}
                                        showPagination={false}
                                    persona="customer"
                                    onEdit={handleEditComment}
                                    onDelete={handleDeleteComment}
                                    />
                                    {isSendbackMode && uploadedFile && uploadedFile?.id && isEditable && (
                                        <CommentToggle
                                            onAddComment={handleAddComment}
                                            persona={"customer"}
                                            buttonText="Comment"
                                            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
                                            hasComments={comments && comments.length > 0}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    
                    {!hideVersionsTab && activeTab === 1 && tabs.length === 3 && (
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        {iframeLoading && (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'flex-start', 
                                minHeight: '500px',
                                width: '100%',
                                zIndex: 1,
                                position: 'absolute',
                                top: 0,
                                left: 0
                            }}>
                                <CircularProgress variant="indeterminate" statusText="Loading..." />
                            </Box>
                        )}
                            <iframe
                                ref={iframeRef}
                                src="https://iqvia-demo.vercel.app"
                                width="100%"
                                scrolling="no"
                                style={{ 
                                    border: 'none',
                                    overflow: 'hidden',
                                    display: 'block',
                                    minHeight: '500px',
                                    height: '500px'
                                }}
                                title="AI Review Agent"
                                onLoad={() => setIframeLoading(false)}
                            />
                        </Box>
                    )}
                    
                    {!hideVersionsTab && ((activeTab === 1 && tabs.length === 2) || (activeTab === 2 && tabs.length === 3)) && (
                            <EctdVersionsHistory 
                                requestId={effectiveRequestId}
                                persona={role === 'customer' || CATEGORIES.COMMITTEE_CATEGORIES.includes(role) ? 'customer' : 'admin'}
                                role={role}
                            />
                    )}
            </Paper>
            <Modal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Confirmation Message"
                message="Are you sure you want to delete this file?"
                buttonProps={[
                    { variant: 'secondary', label: 'Cancel' },
                    { label: 'Confirm', onClick: handleDeleteFile },
                ]}
            />
        </div>);
};

export default Ectd;